import { useEffect, useState } from 'react';

import { Box, Card, Link, Stack, TextField, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import BottomSheet from '@/designs-dev/device/BottomSheet';
import StepScreen from '@/karehero-app/access/StepScreen';
import { type Account } from '@/karehero-app/access/AccountBar';

// Code entry after the work email step. Resend stays inline (the common
// case); the rarer rescues (payroll ID, sign in instead) sit behind a
// single "Having trouble?" link that opens a bottom sheet, so the screen
// stays calm.

const RESENT_MS = 4000;

// A tappable rescue option in the trouble sheet.
const TroubleOption = ({
  icon,
  label,
  desc,
  onClick,
}: {
  icon: string;
  label: string;
  desc: string;
  onClick: () => void;
}) => (
  <Card variant='listItem' onClick={onClick} sx={{ p: 2 }}>
    <Stack sx={{ alignItems: 'center' }} direction='row' spacing={1.5}>
      <Icon icon={icon as never} fontSize={18} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant='h6'>{label}</Typography>
        <Typography variant='body2' color='text.secondary'>
          {desc}
        </Typography>
      </Box>
      <Icon icon='chevron-right' fontSize={14} />
    </Stack>
  </Card>
);

const CodeScreen = ({
  email,
  account,
  onVerify,
  onUsePayrollId,
  onSignIn,
  onBack,
}: {
  email?: string;
  /** The step sits after the account exists, so it carries the bar. */
  account?: Account;
  onVerify: (code: string) => void;
  onUsePayrollId: () => void;
  onSignIn: () => void;
  onBack: () => void;
}) => {
  const [code, setCode] = useState('');
  const [isResent, setIsResent] = useState(false);
  const [isTroubleOpen, setIsTroubleOpen] = useState(false);

  useEffect(() => {
    if (!isResent) return;
    const timer = window.setTimeout(() => setIsResent(false), RESENT_MS);
    return () => window.clearTimeout(timer);
  }, [isResent]);

  return (
    <StepScreen
      title='Check your email'
      description={
        email
          ? `We sent a 6-digit code to ${email}.`
          : 'We sent a 6-digit code to your email.'
      }
      account={account}
      primaryLabel='Verify code'
      onPrimary={() => onVerify(code.trim())}
      onBack={onBack}
      footnote={
        <Link
          component='button'
          type='button'
          variant='body2'
          onClick={() => setIsTroubleOpen(true)}
        >
          Having trouble?
        </Link>
      }
    >
      <Stack spacing={1.5}>
        <TextField
          slotProps={{ inputLabel: { shrink: true } }}
          label='6-digit code'
          placeholder='••••••'
          variant='filled'
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode='numeric'
          autoComplete='one-time-code'
          fullWidth
        />
        {isResent ? (
          <Typography variant='body2' color='text.secondary'>
            Code sent. Check your inbox.
          </Typography>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            Didn&apos;t get it?{' '}
            <Link
              component='button'
              type='button'
              variant='body2'
              onClick={() => setIsResent(true)}
              sx={{ verticalAlign: 'baseline' }}
            >
              Resend code
            </Link>
          </Typography>
        )}
      </Stack>

      {isTroubleOpen && (
        <BottomSheet
          variant='hug'
          showHandleClose={false}
          onClose={() => setIsTroubleOpen(false)}
        >
          <Stack sx={{ pb: 1 }} spacing={2}>
            <Box
              sx={{
                alignSelf: 'center',
                width: 36,
                height: 4,
                borderRadius: '2px',
                bgcolor: 'custom.neutral.300',
              }}
            />
            <Typography variant='h4'>Having trouble?</Typography>
            <Stack spacing={1}>
              <TroubleOption
                icon='file-contract'
                label='Use your payroll ID instead'
                desc='Verify with the ID on your payslip.'
                onClick={onUsePayrollId}
              />
              <TroubleOption
                icon='user'
                label='Sign in instead'
                desc='Got an email saying you already have an account? Sign in to use it.'
                onClick={onSignIn}
              />
            </Stack>
          </Stack>
        </BottomSheet>
      )}
    </StepScreen>
  );
};

export default CodeScreen;
